import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import {
  PAYMENT_METHODS,
  formatDateHuman,
  formatMoneyMinor,
  type InvoiceDetailRow,
} from "@themislex/shared";
import { api } from "../../api/client";
import { useApi } from "../../api/hooks";
import { amountToNumber } from "../../lib/parse";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Input,
  InvoiceStatusBadge,
  Modal,
  Money,
  PageHeader,
  Select,
  Spinner,
} from "../../components/ui";

const METHOD_LABELS: Record<string, string> = {
  upi: "UPI",
  bank_transfer: "Bank transfer",
  cash: "Cash",
  cheque: "Cheque",
  other: "Other",
};

export default function InvoiceDetail() {
  const { id = "" } = useParams();
  const { data, error, loading, refetch } = useApi<{ invoice: InvoiceDetailRow }>(
    `/api/invoices/${id}`,
  );

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("upi");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payReference, setPayReference] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState<string>();

  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidBusy, setMarkPaidBusy] = useState(false);
  const [actionError, setActionError] = useState<string>();

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorNote>{error ?? "Invoice not found."}</ErrorNote>;
  }

  const invoice = data.invoice;
  const isSettled = invoice.displayStatus === "paid";
  const isDraft = invoice.displayStatus === "draft";
  const openPaymentModal = () => {
    setPayAmount(
      invoice.outstandingMinor > 0 ? (invoice.outstandingMinor / 100).toFixed(2) : "",
    );
    setPayError(undefined);
    setPayOpen(true);
  };

  const recordPayment = async (e: FormEvent) => {
    e.preventDefault();
    setPayError(undefined);
    const amount = amountToNumber(payAmount);
    if (amount === undefined || amount <= 0) {
      setPayError("Enter a valid payment amount.");
      return;
    }
    setPayBusy(true);
    try {
      await api(`/api/invoices/${id}/payments`, {
        method: "POST",
        json: {
          amount,
          method: payMethod,
          date: payDate || undefined,
          referenceId: payReference.trim() || undefined,
        },
      });
      setPayOpen(false);
      await refetch();
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Could not record the payment.");
    } finally {
      setPayBusy(false);
    }
  };

  const markPaid = async () => {
    setMarkPaidBusy(true);
    try {
      await api(`/api/invoices/${id}/mark-paid`, { method: "POST" });
      setMarkPaidOpen(false);
      await refetch();
    } catch (err) {
      setMarkPaidOpen(false);
      setActionError(err instanceof Error ? err.message : "Could not mark the invoice as paid.");
    } finally {
      setMarkPaidBusy(false);
    }
  };

  const summary: { label: string; value: React.ReactNode }[] = [
    {
      label: "Client",
      value: (
        <Link to={`/desk/clients/${invoice.clientId}`} className="text-accent-text hover:underline">
          {invoice.clientName}
        </Link>
      ),
    },
    {
      label: "Matter",
      value: (
        <Link to={`/desk/matters/${invoice.matterId}`} className="text-accent-text hover:underline">
          {invoice.matterTitle}
        </Link>
      ),
    },
    { label: "Issued", value: formatDateHuman(invoice.issuedDate) },
    { label: "Due", value: formatDateHuman(invoice.dueDate) },
    { label: "Paid on", value: formatDateHuman(invoice.paidDate) },
    { label: "Amount", value: <Money minor={invoice.amountMinor} /> },
    { label: "Received", value: <Money minor={invoice.paidMinor} /> },
    {
      label: "Outstanding",
      value: <Money minor={invoice.outstandingMinor} className="font-semibold" />,
    },
  ];

  return (
    <>
      <PageHeader
        title={`Invoice ${invoice.invoiceNumber}`}
        subtitle="Raise, track and settle."
        actions={
          <>
            <a
              className="btn btn-secondary no-underline"
              href={`/api/invoices/${invoice.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download PDF
            </a>
            {!isSettled && !isDraft && (
              <>
                <Button onClick={openPaymentModal}>Record payment</Button>
                <Button variant="primary" onClick={() => setMarkPaidOpen(true)}>
                  Mark paid
                </Button>
              </>
            )}
            {!isSettled && (
              <Link to={`/desk/invoices/${invoice.id}/edit`} className="btn btn-ghost no-underline">
                Edit
              </Link>
            )}
          </>
        }
      />

      {actionError && (
        <div className="mb-6">
          <ErrorNote>{actionError}</ErrorNote>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Summary" className="lg:col-span-2">
          <div className="mb-4 flex items-center gap-3">
            <InvoiceStatusBadge status={invoice.displayStatus} />
            {isDraft && <Badge variant="neutral">Not visible to client</Badge>}
          </div>
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {summary.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {row.label}
                </dt>
                <dd className="text-sm">{row.value ?? "—"}</dd>
              </div>
            ))}
          </dl>
          {invoice.notes && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Notes</p>
              <p className="mt-1 whitespace-pre-line text-sm text-muted">{invoice.notes}</p>
            </div>
          )}
        </Card>

        <Card title="Payments">
          {invoice.payments.length === 0 ? (
            <p className="text-sm text-muted">No payments recorded against this invoice.</p>
          ) : (
            <ul className="space-y-3">
              {invoice.payments.map((payment) => (
                <li key={payment.id} className="flex items-start justify-between gap-3 border-b border-border pb-3 last:border-0 last:pb-0">
                  <div>
                    <p className="text-sm font-medium">
                      {formatMoneyMinor(payment.amountMinor)}
                    </p>
                    <p className="text-xs text-muted">
                      {formatDateHuman(payment.paymentDate)} ·{" "}
                      {METHOD_LABELS[payment.paymentMethod] ?? payment.paymentMethod}
                      {payment.referenceId ? ` · ${payment.referenceId}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <Card title="Line items">
          {invoice.items.length === 0 ? (
            <p className="text-sm text-muted">
              This invoice was raised with a single amount and has no itemised lines.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Description</th>
                    <th scope="col" className="!text-right">
                      Qty
                    </th>
                    <th scope="col" className="!text-right">
                      Unit amount
                    </th>
                    <th scope="col" className="!text-right">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td className="text-right tabular-nums">{item.quantity}</td>
                      <td className="text-right tabular-nums">
                        <Money minor={item.unitAmountMinor} />
                      </td>
                      <td className="text-right font-medium tabular-nums">
                        <Money minor={item.amountMinor} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-sm font-medium">
                      Total
                    </td>
                    <td className="px-3 py-2 text-right font-heading text-base font-bold">
                      <Money minor={invoice.amountMinor} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title="Record payment">
        <form onSubmit={recordPayment} noValidate>
          {payError && (
            <div className="mb-4">
              <ErrorNote>{payError}</ErrorNote>
            </div>
          )}
          <div className="space-y-4">
            <Input
              label="Amount received (₹)"
              inputMode="decimal"
              required
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              hint={`Outstanding: ${formatMoneyMinor(invoice.outstandingMinor)}`}
            />
            <Select label="Method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              {PAYMENT_METHODS.map((method) => (
                <option key={method} value={method}>
                  {METHOD_LABELS[method] ?? method}
                </option>
              ))}
            </Select>
            <Input
              label="Payment date"
              type="date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
            <Input
              label="Reference (optional)"
              placeholder="UPI ref / cheque no."
              value={payReference}
              onChange={(e) => setPayReference(e.target.value)}
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" onClick={() => setPayOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={payBusy}>
              Record payment
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={markPaidOpen}
        onClose={() => setMarkPaidOpen(false)}
        title="Mark invoice as paid"
      >
        <p className="text-sm text-muted">
          Marking this invoice as paid will record a balancing payment of{" "}
          <strong>{formatMoneyMinor(invoice.outstandingMinor)}</strong> (method:
          “Other”). Prefer “Record payment” when you know the actual amount and
          method.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={() => setMarkPaidOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={markPaidBusy} onClick={() => void markPaid()}>
            Confirm, mark paid
          </Button>
        </div>
      </Modal>
    </>
  );
}
